/**
 * Email Signup Block — Klaviyo client subscription (public API key + list ID).
 * @see https://developers.klaviyo.com/en/reference/create_client_subscription
 */
// @ts-nocheck

const KLAVIYO_SUBSCRIPTIONS_URL = 'https://a.klaviyo.com/client/subscriptions/';
/** @see https://developers.klaviyo.com/en/reference/create_client_subscription */
const KLAVIYO_REVISION = '2026-04-15';

/**
 * Generic, country-agnostic phone normalizer → E.164.
 * Updated to default to North America (+1) if exactly 10 digits are provided.
 * @param {string} raw
 * @returns {string} E.164 string (e.g. `+15551234567`) or '' if invalid/empty.
 */
function normalizePhoneToE16401(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';

  let cleaned = trimmed.replace(/[\s\-().]/g, '');

  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`;
  }


  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1).replace(/\D/g, '');

    if (digits.length === 10) {
      return `+1${digits}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }
    return '';
  }

  const digitsOnly = cleaned.replace(/\D/g, '');

  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return `+${digitsOnly}`;
  }

  return '';
}

function isE164String(value) {
  return typeof value === 'string' && /^\+[1-9]\d{7,14}$/.test(value);
}

function isValidPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

function klaviyoSubscriptionHeaders() {
  return {
    'Content-Type': 'application/vnd.api+json',
    revision: KLAVIYO_REVISION,
  };
}

/** Strings from `Theme.translations` (see snippets/scripts.liquid — `blocks.email_signup.*`). */
function t(key) {
  if (typeof Theme !== 'undefined' && Theme.translations && Theme.translations[key]) {
    return Theme.translations[key];
  }
  return '';
}

async function klaviyoErrorMessageFromResponse(response) {
  const fallback = t('newsletter_error_generic');
  try {
    const errBody = await response.json();
    const detail = errBody?.errors?.[0]?.detail;
    return typeof detail === 'string' && detail.length > 0 ? detail : fallback;
  } catch {
    return fallback;
  }
}

class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.blockId = this.getAttribute('data-block-id') || '';
    this.listId = (this.getAttribute('data-newsletter-list-id') || '').trim();
    this.requirePhone = String(this.getAttribute('data-require-phone')).toLowerCase() === 'true';

    this.errorContainer = this.querySelector(`#newsletter-error-${this.blockId}`);
    this.errorText = this.errorContainer?.querySelector('.email-signup__message-text--error');
    this.phoneErrorContainer = this.querySelector(`#newsletter-phone-error-${this.blockId}`);
    this.phoneErrorText = this.phoneErrorContainer?.querySelector('.email-signup__message-text--phone-error');
    this.successContainer = this.querySelector(`#newsletter-subscribe-thank-you-${this.blockId}`);

    this.inputs = this.querySelectorAll('[data-input-field]');

    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit);
    }

    this.inputs.forEach((input) => {
      if (input.value) {
        input.parentElement.setAttribute('data-has-value', '');
      }
    });

    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleFormSubmit);
    }
  }

  setupEventListeners() {
    this.inputs.forEach((input) => {
      if (input) {
        input.addEventListener('input', () => {
          this.hideError();
          this.handleChange(input);
        });
        input.addEventListener('invalid', () => {
          this.checkValidity(input);
        });
      }
    });
  }

  hideError() {
    if (this.errorContainer) {
      this.errorContainer.hidden = true;
    }
    if (this.phoneErrorContainer) {
      this.phoneErrorContainer.hidden = true;
    }
    this.querySelectorAll('.email-signup__input-group[data-invalid]').forEach((el) => {
      el.removeAttribute('data-invalid');
    });
  }

  showError(message) {
    if (this.errorText) {
      this.errorText.textContent = message;
    }
    if (this.errorContainer) {
      this.errorContainer.hidden = false;
    }
  }

  showPhoneError(message) {
    if (this.phoneErrorText) {
      this.phoneErrorText.textContent = message;
    }
    if (this.phoneErrorContainer) {
      this.phoneErrorContainer.hidden = false;
    }
  }

  /** Route the error message to the container sitting under the input that failed. */
  showFieldError(input, message) {
    if (input && (input.getAttribute('type') || '').toLowerCase() === 'tel') {
      this.showPhoneError(message);
    } else {
      this.showError(message);
    }
  }

  handleChange(input) {
    const value = input.value.trim();

    if (value) {
      input.parentElement.setAttribute('data-has-value', '');
      input.setCustomValidity('');
      input.parentElement.removeAttribute('data-invalid');
    } else {
      input.parentElement.removeAttribute('data-has-value');
      if (input.hasAttribute('required')) {
        const tInput = (input.getAttribute('type') || '').toLowerCase();
        const requiredMsg =
          tInput === 'email'
            ? t('newsletter_required_email')
            : tInput === 'tel'
              ? t('newsletter_required_phone')
              : t('newsletter_required_field');
        input.setCustomValidity(requiredMsg || t('newsletter_required_field'));
      } else {
        input.setCustomValidity('');
      }
    }
  }

  async handleFormSubmit(e) {
    e.preventDefault();
    this.hideError();

    const emailInput = this.querySelector(`#email-address-${this.blockId}`);
    let isValid = true;
    const invalidInputs = [];

    this.inputs.forEach((input) => {
      if (!this.checkValidity(input)) {
        isValid = false;
        invalidInputs.push(input);
      }
    });

    if (!isValid) {
      invalidInputs.forEach((input) => {
        const msg =
          input.validationMessage && input.validationMessage.trim()
            ? input.validationMessage
            : t('newsletter_error_generic');
        this.showFieldError(input, msg);
      });

      if (invalidInputs[0]) {
        invalidInputs[0].focus();
      }

      return;
    }

    const email = emailInput ? emailInput.value.trim() : '';
    const phoneInput = this.querySelector(`#phone-number-${this.blockId}`);
    const phoneRaw = phoneInput ? phoneInput.value.trim() : '';
    const phoneE164 = phoneRaw ? normalizePhoneToE16401(phoneRaw) : '';

    const publicKey =
      typeof Theme !== 'undefined' && Theme.otterVars && Theme.otterVars.klaviyoApiKey
        ? String(Theme.otterVars.klaviyoApiKey).trim()
        : '';

    if (!publicKey || !this.listId) {
      this.showError(t('newsletter_error_config'));
      return;
    }

    // Optional phone: skip digit check when empty; required empty is blocked in checkValidity.
    if (phoneRaw && !isValidPhone(phoneRaw)) {
      const phoneEl = this.querySelector(`#phone-number-${this.blockId}`);
      this.showPhoneError(t('newsletter_required_phone'));
      if (phoneEl) {
        phoneEl.parentElement?.setAttribute('data-invalid', '');
        phoneEl.focus({ preventScroll: false });
      }
      return;
    }

    const profileAttributes = {
      email,
    };
    if (phoneE164) {
      profileAttributes.phone_number = phoneE164;
      // Explicit channels so SMS consent is recorded when phone is present (see Klaviyo Create Client Subscription).
      profileAttributes.subscriptions = {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
        sms: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      };
    }
    try {
      // Sends the below custom attributes with Klaviyo subscription 
      // 1. consent to marketing tracking
      // 2. customer locale (language and country)
      profileAttributes.properties = {
        'site_locale': Shopify && Shopify.country && Shopify.locale ? Shopify.locale + '-' + Shopify.country : 'en-US',
        'CookieConsent': otterConsent && otterConsent._pendingState ? otterConsent._pendingState.marketing : true
      };
    } catch (e) {}
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Shopify theme newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    };

    const url = `${KLAVIYO_SUBSCRIPTIONS_URL}?company_id=${encodeURIComponent(publicKey)}`;

    if (this.submitButton) {
      this.submitButton.disabled = true;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: klaviyoSubscriptionHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.status === 202 || response.ok) {
        this.classList.add('email-signup--submitted');
        this.successContainer?.focus({ preventScroll: false });
        return;
      }

      this.showError(await klaviyoErrorMessageFromResponse(response));
    } catch (err) {
      console.error('Klaviyo newsletter submit error:', err);
      this.showError(t('newsletter_error_generic'));
    } finally {
      if (this.submitButton) {
        this.submitButton.disabled = false;
      }
    }
  }

  checkValidity(input) {
    const value = input.value.trim();
    const type = input.getAttribute('type');

    if (input.hasAttribute('required') && !value) {
      let requiredMsg = '';
      if (type === 'email') requiredMsg = t('newsletter_required_email');
      else if (type === 'tel') requiredMsg = t('newsletter_required_phone');
      if (!requiredMsg) requiredMsg = t('newsletter_required_field');
      input.setCustomValidity(requiredMsg);
      input.parentElement.setAttribute('data-invalid', '');
      return false;
    }

    if (type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        input.setCustomValidity(t('newsletter_invalid_email'));
        input.parentElement.setAttribute('data-invalid', '');
        return false;
      }
    }

    if (type === 'tel' && value) {
      if (!isValidPhone(value)) {
        input.setCustomValidity(t('newsletter_required_phone'));
        input.parentElement.setAttribute('data-invalid', '');
        return false;
      }
    }

    input.setCustomValidity('');
    input.parentElement.removeAttribute('data-invalid');

    if (value) {
      input.parentElement.setAttribute('data-has-value', '');
    } else {
      input.parentElement.removeAttribute('data-has-value');
    }

    return true;
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
